import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';
import { add, isAfter, startOfHour } from 'date-fns';
import { NGXLogger } from 'ngx-logger';
import { BehaviorSubject, combineLatest, interval, Observable, of, Subject } from 'rxjs';
import { distinctUntilChanged, map, pluck, shareReplay, switchMap, take, throttleTime } from 'rxjs/operators';
import { DRIVER_ID } from '../drivers/driver-routing.constants';
import { SnackBarService } from '../shared/snackbar.service';
import {
  ActiveDriver,
  CreateDriverForApplicantRequestRetool,
  DriverCertification,
  DriverDetail,
  DriverHSEScore,
  DriverListResponse,
  DriverPriority,
  DriverWithRegion,
  HSEScore,
  ListAvailableDriversResponseDriver,
  LoHiLoadsListItem,
  LoHiShiftListItem,
  PendingDriver,
  RejectedLoads,
  ReportImprovements,
  ShiftSchedulingStats,
  Stats,
  UserInvite,
} from '../types';
import { distinctUntilNullableKeyChanged } from '../utilities/distinctUntilRobustKeyChange';
import { AuthService } from './auth.service';
import { FeatureFlagService } from './feature-flags.service';
import { RouterStateService } from './router-state.service';

@Injectable({
  providedIn: 'root',
})
export class DriversService {
  private shiftFilterDate$$ = new BehaviorSubject<Date>(null);
  public shiftFilterDate$ = this.shiftFilterDate$$.pipe(distinctUntilChanged(), shareReplay(1));

  private driverList$$ = new BehaviorSubject<DriverListResponse>(null);
  private driverListThrottle$$ = new Subject();
  private activeDriverListShared$: Observable<ActiveDriver[]> = this.driverList$$.asObservable().pipe(
    distinctUntilChanged(),
    map((driverList) => {
      return driverList?.activeDrivers ? driverList.activeDrivers : [];
    }),
    shareReplay(1),
  );
  private activeDriverListFilterByDateShared$: Observable<ActiveDriver[]> = combineLatest([
    this.shiftFilterDate$$,
    this.activeDriverListShared$,
  ]).pipe(
    map(([date, drivers]) => {
      if (!date) {
        return drivers;
      }
      return drivers.map(copyDriverWithoutShiftsNotSpanningDate(date)).filter(doDriversHaveShiftAt(date));
    }),
    shareReplay(1),
  );

  private pendingDriverListShared$: Observable<PendingDriver[]> = this.driverList$$.asObservable().pipe(
    distinctUntilChanged(),
    map((driverList) => (driverList?.pendingDrivers ? driverList.pendingDrivers : [])),
    shareReplay(1),
  );

  public get activeDriverList$(): Observable<ActiveDriver[]> {
    this.driverListThrottle$$.next();
    return this.activeDriverListShared$;
  }

  public get pendingDriverList$(): Observable<PendingDriver[]> {
    this.driverListThrottle$$.next();
    return this.pendingDriverListShared$;
  }

  public get dateFilteredActiveDriverList$(): Observable<ActiveDriver[]> {
    this.driverListThrottle$$.next();
    return this.activeDriverListFilterByDateShared$;
  }

  public get dateFilteredPendingDriverList$(): Observable<PendingDriver[]> {
    this.driverListThrottle$$.next();
    return this.pendingDriverListShared$;
  }

  private currentDriverShared$: Observable<ActiveDriver | PendingDriver> = combineLatest([
    this.driverList$$,
    this.routerState.listenForParamChange$(DRIVER_ID),
  ]).pipe(
    map(([driverLists, driverId]) => {
      if (!driverLists || !driverId) {
        return null;
      }
      const foundActive = driverLists?.activeDrivers?.find((driver) => driver.id === driverId);
      if (foundActive) {
        this.loadDriverStats(driverId);
        this.loadDriverDetail(driverId);
        this.loadHSEScore(driverId);
        return foundActive;
      }
      const foundPending = driverLists?.pendingDrivers?.find((driver) => driver.id === driverId);
      return foundPending;
    }),
    shareReplay(1),
  );

  private driverStats$$ = new BehaviorSubject<Stats>(null);
  private driverStatsShared$ = this.driverStats$$.asObservable();

  public get driverStats$(): Observable<Stats> {
    return this.driverStatsShared$;
  }

  private driverHSEScore$$ = new BehaviorSubject<DriverHSEScore>(null);
  private driverHSEScoreShared$ = this.driverHSEScore$$.asObservable();

  public get driverHSEScore$(): Observable<DriverHSEScore> {
    return this.driverHSEScoreShared$;
  }

  private driverCoaching$$ = new BehaviorSubject<Stats>(null);
  private driverCoachingThrottle$$ = new Subject();
  private driverCoachingShared$ = this.driverCoaching$$.asObservable();

  public get driverCoaching$(): Observable<Stats> {
    this.driverCoachingThrottle$$.next();
    return this.driverCoachingShared$;
  }

  private driversWithRegion$$ = new BehaviorSubject<DriverWithRegion[]>(null);
  private driversWithRegionThrottle$$ = new Subject();
  private driversWithRegionShared$ = this.driversWithRegion$$.asObservable();

  public get driversWithRegion$(): Observable<DriverWithRegion[]> {
    this.driversWithRegionThrottle$$.next();
    return this.driversWithRegionShared$;
  }

  private driverDetails$$ = new BehaviorSubject<DriverDetail>(null);
  private driverDetailsThrottle$$ = new Subject();
  private driverDetailsShared$ = this.driverDetails$$.asObservable();

  public get driverDetails$(): Observable<DriverDetail> {
    this.driverDetailsThrottle$$.next();
    return this.driverDetailsShared$;
  }

  private driverStatsLoads$$ = new BehaviorSubject<RejectedLoads>(null);
  private driverStatsThrottleLoads$$ = new Subject();
  private driverStatsSharedLoads$ = this.driverStatsLoads$$.asObservable();

  public get driverStatsLoads$(): Observable<RejectedLoads> {
    this.driverStatsThrottleLoads$$.next();
    return this.driverStatsSharedLoads$;
  }

  private shiftStatsDrivers$$ = new BehaviorSubject<ShiftSchedulingStats>(null);
  private shiftStatsDriversThrottle$ = new Subject();
  private shiftStatsDriversShared$ = this.shiftStatsDrivers$$.asObservable();

  public get shiftStatsDrivers$(): Observable<ShiftSchedulingStats> {
    this.shiftStatsDriversThrottle$.next();
    return this.shiftStatsDriversShared$;
  }

  public get currentDriver$(): Observable<ActiveDriver | PendingDriver> {
    this.driverListThrottle$$.next();
    return this.currentDriverShared$;
  }

  private companyTarget$$ = new BehaviorSubject<number>(null);
  private companyTargetThrottle$$ = new Subject();
  private companyTargetShared$ = this.companyTarget$$.asObservable().pipe(shareReplay(1));

  public get companyTarget$(): Observable<number> {
    this.companyTargetThrottle$$.next();
    return this.companyTargetShared$;
  }

  private availableDrivers$$ = new BehaviorSubject<ListAvailableDriversResponseDriver[]>([]);
  private availableDriversThrottle$ = new Subject();
  private availableDriversShared$ = this.availableDrivers$$.asObservable();

  public get availableDrivers$(): Observable<ListAvailableDriversResponseDriver[]> {
    this.availableDriversThrottle$.next();
    return this.availableDriversShared$;
  }

  private availableDriversNow$$ = new BehaviorSubject<ListAvailableDriversResponseDriver[]>([]);
  private availableDriversNowThrottle$ = new Subject();
  private availableDriversNowShared$ = this.availableDriversNow$$.asObservable();

  public get availableDriversNow$(): Observable<ListAvailableDriversResponseDriver[]> {
    this.availableDriversNowThrottle$.next();
    return this.availableDriversNowShared$;
  }

  public currentDriverCompletedLoads$: Observable<LoHiLoadsListItem[]> = this.currentDriver$.pipe(
    distinctUntilNullableKeyChanged('id'),
    switchMap((driver) => {
      if (!driver) {
        return of([]);
      }
      return this.getCompletedLoadsForDriver$(driver.id);
    }),
    shareReplay(1),
  );

  private driverCerts$$ = new BehaviorSubject<DriverCertification[]>([]);
  private driverCertsThrottle$$ = new Subject();
  private driverCertsShared$ = this.driverCerts$$.asObservable();

  public get driverCerts$(): Observable<DriverCertification[]> {
    this.driverCertsThrottle$$.next();
    return this.driverCertsShared$;
  }

  private driverSafetyScores$$ = new BehaviorSubject<Record<string, HSEScore>>(null);
  private driverSafetyScoresThrottle$$ = new Subject();
  private driverSafetyScoresShared$ = this.driverSafetyScores$$.asObservable();

  public get driverSafetyScores$(): Observable<Record<string, HSEScore>> {
    this.driverSafetyScoresThrottle$$.next();
    return this.driverSafetyScoresShared$;
  }

  constructor(
    private httpClient: HttpClient,
    private logger: NGXLogger,
    private routerState: RouterStateService,
    private authService: AuthService, // For refreshing after changing the driver booking bool
    private snackBar: SnackBarService,
    private featureFlagService: FeatureFlagService,
  ) {
    this.driverListThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriverList();
    });
    this.driverStatsThrottleLoads$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriverStatsRejectedLoads();
    });
    this.shiftStatsDriversThrottle$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriverStatsScheduling();
    });
    this.driverCoachingThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriverCoaching();
    });
    this.companyTargetThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadCompanyTarget();
    });
    this.availableDriversThrottle$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadAvailableDrivers();
    });
    this.availableDriversThrottle$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadAvailableDriversNow();
    });
    this.driverCertsThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriverCerts();
    });
    this.driverSafetyScoresThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriverSafetyScores();
    });
    this.driversWithRegionThrottle$$.pipe(throttleTime(1000)).subscribe(() => {
      this.loadDriversWithRegion();
    });
    interval(5 * 60 * 1000).subscribe(() => {
      if (environment.pollingEnabled) {
        this.driverListThrottle$$.next();
      }
    });
  }

  public setDateFilter(date: Date) {
    this.shiftFilterDate$$.next(date);
    this.loadAvailableDrivers();
  }

  public async inviteUser(invite: UserInvite): Promise<boolean> {
    try {
      await this.httpClient.post(`${environment.api}/v1/invite_user`, invite).toPromise();
      this.driverListThrottle$$.next();
      return true;
    } catch (error) {
      this.logger.error(error);
      if (error instanceof HttpErrorResponse) {
        if (error.error.isClientError) {
          this.snackBar.showError(error.error.messageToUser);
        }
      }

      return false;
    }
  }

  public async invite5fTourUser(invite: CreateDriverForApplicantRequestRetool): Promise<boolean> {
    try {
      await this.httpClient.put(`${environment.api}/v2/max_payloop/owner/create_5f_tour_driver`, invite).toPromise();
      this.driverListThrottle$$.next();
      return true;
    } catch (error) {
      this.logger.error(error);
      if (error instanceof HttpErrorResponse) {
        if (error.error.isClientError) {
          this.snackBar.showError(error.error.messageToUser);
        }
      }

      return false;
    }
  }

  public async removeUser(userId: string): Promise<boolean> {
    try {
      await this.httpClient.post(`${environment.api}/v1/remove_user/${userId}`, {}).toPromise();
      this.driverListThrottle$$.next();
      return true;
    } catch (error) {
      this.logger.error(error);
      if (error instanceof HttpErrorResponse) {
        if (error.error.isClientError) {
          this.snackBar.showError(error.error.messageToUser);
        } else {
          this.snackBar.showError('Failed to remove user');
        }
      }
      return false;
    }
  }

  public async removePendingUser(userId: string): Promise<boolean> {
    try {
      await this.httpClient.post(`${environment.api}/v1/remove_pending_user/${userId}`, {}).toPromise();
      this.driverListThrottle$$.next();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async getDefaultDriverPercentage(): Promise<number> {
    try {
      const result = await this.httpClient
        .get<{ cut: number; cutFloat: number }>(`${environment.api}/v1/admin/company_default_driver_cut`)
        .toPromise();
      return result.cutFloat;
    } catch (error) {
      this.logger.error(error);
      return null;
    }
  }

  private async loadCompanyTarget() {
    try {
      const result = await this.httpClient
        .get<{ target: number }>(`${environment.api}/v1/admin/company_target`)
        .toPromise();
      this.companyTarget$$.next(result.target);
    } catch (error) {
      this.logger.error(error);
    }
  }

  public async setDefaultDriverPercentage(percentage: number): Promise<boolean> {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/company_default_driver_cut`, { cutFloat: percentage })
        .toPromise();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async setDriverPercentage(driverId: string, percentage: number): Promise<boolean> {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/driver_cut`, { driverId, cutFloat: percentage })
        .toPromise();
      await this.loadDriverList();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async setDriverPassword(userId: string, newPassword: string): Promise<boolean> {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/reset_driver_password`, { userId, newPassword })
        .toPromise();
      return true;
    } catch (error) {
      this.logger.error(error);
      if (error instanceof HttpErrorResponse) {
        if (error.error.isClientError) {
          this.snackBar.showError(error.error.messageToUser);
        }
      }
      return false;
    }
  }

  public async setDriverCDL(driverId: string, cdl: string): Promise<boolean> {
    try {
      await this.httpClient.put(`${environment.api}/v1/admin/cdl_number`, { driverId, cdl }).toPromise();
      await this.loadDriverList();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async setDriverEmployeeId(driverId: string, employeeId: string): Promise<boolean> {
    try {
      await this.httpClient
        .put<null>(`${environment.api}/v1/admin/employee_id`, { driverId, employeeId })
        .toPromise();
      await this.loadDriverList();
      return true;
    } catch (error) {
      this.logger.error(error);
      if (error instanceof HttpErrorResponse) {
        if (error.error.isClientError) {
          this.snackBar.showError(error.error.messageToUser);
        } else {
          this.snackBar.showError('Failed to update Employee ID');
        }
      }
      return false;
    }
  }

  public async setDriverTarget(driverId: string, target: number): Promise<boolean> {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/driver_target`, { driverId, target })
        .toPromise();
      await this.loadDriverList();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async setCompanyTarget(target: number) {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/company_target`, { target })
        .toPromise();
      await this.loadCompanyTarget();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  // This probably doesn't belong here I am leaving it here for now, to the person reading this: do better than me
  public async setAllowDriverBooking(allowDriverBooking: boolean): Promise<boolean> {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/toggle_driver_booking`, { allowDriverBooking })
        .toPromise();
      await this.authService.loadUserInfo();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async setAllowDriverTruck(allowDriverTruckCreate: boolean): Promise<boolean> {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/toggle_driver_truck`, { allowDriverTruckCreate })
        .toPromise();
      await this.authService.loadUserInfo();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async setAllowDriverTrailer(allowDriverTrailerCreate: boolean): Promise<boolean> {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/toggle_driver_trailer`, { allowDriverTrailerCreate })
        .toPromise();
      await this.authService.loadUserInfo();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async setAllowDriversToViewTourDollarAmounts(allowDriversToViewTourDollarAmounts: boolean): Promise<boolean> {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/toggle_drivers_can_view_tour_dollar_amounts`, {
          allowDriversToViewTourDollarAmounts,
        })
        .toPromise();
      await this.authService.loadUserInfo();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async setAllowDriversToViewTourRateCon(allowDriversToReviewRateCon: boolean): Promise<boolean> {
    try {
      await this.httpClient
        .put<null>(`${environment.api}/v1/admin/toggle_drivers_can_view_rate_con`, {
          allowDriversToReviewRateCon,
        })
        .toPromise();
      await this.authService.loadUserInfo();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async setAllowDriversToApproveTours(allowDriversToApproveTours: boolean): Promise<boolean> {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/toggle_drivers_can_approve_tours`, { allowDriversToApproveTours })
        .toPromise();
      await this.authService.loadUserInfo();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async getDriverCoachingMessage(driverUserId: string, coachingCategory: number): Promise<string> {
    try {
      const response = await this.httpClient
        .post<{ textMessageBody: string }>(`${environment.api}/v1/admin/coach_driver_via_sms`, {
          driverUserId,
          coachingCategory,
        })
        .toPromise();
      return response.textMessageBody;
    } catch (error) {
      return Promise.reject(error);
    }
  }

  public async getDriverImprovements(driverUserId: string): Promise<ReportImprovements> {
    const response = await this.httpClient
      .get<{ improvements: ReportImprovements }>(`${environment.api}/v1/admin/improvement_report/${driverUserId}`)
      .toPromise();
    return response.improvements;
  }

  public async setPriority(driverId: string, reason: string): Promise<boolean> {
    try {
      await this.httpClient
        .post<{ updatedPriority: DriverPriority }>(`${environment.api}/v1/admin/priority_driver_start`, {
          driverId,
          reason,
        })
        .toPromise();
      this.loadDriverList();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async unsetPriority(priorityId: number, reason: string): Promise<boolean> {
    try {
      await this.httpClient
        .post<{ updatedPriority: DriverPriority }>(`${environment.api}/v1/admin/priority_driver_end`, {
          priorityId,
          reason,
        })
        .toPromise();
      this.loadDriverList();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async setAllowShiftBookingByUser(
    userId: string,
    allowShiftBooking: boolean,
    removeTrailerFee: boolean,
  ): Promise<boolean> {
    try {
      await this.httpClient
        .post<{ allowShiftBooking: boolean; companyId: string; userId: string }>(
          `${environment.api}/v1/admin/set_user_company_setting`,
          { allowShiftBooking, removeTrailerFee, userId },
        )
        .toPromise();
      await this.loadDriverList();
      return true;
    } catch (error) {
      this.logger.error(error);
      return false;
    }
  }

  public async updateHos(phoneNumber: string, shiftId: number, hosMinutes: number) {
    try {
      await this.httpClient
        .post<null>(`${environment.api}/v1/admin/update_driver_hos`, {
          hosMinutes,
          phoneNumber,
          shiftId,
        })
        .toPromise();
      await this.loadDriverList();
    } catch (error) {
      this.logger.error(error);
      this.snackBar.showError(error?.error?.messageToUser || 'Failed to update HOS');
    }
  }

  private async loadDriverList() {
    try {
      const results = await this.httpClient.get<DriverListResponse>(`${environment.api}/v1/users`).toPromise();
      this.driverList$$.next(results);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private getCompletedLoadsForDriver$(userId: string): Observable<LoHiLoadsListItem[]> {
    return this.httpClient
      .get<LoHiLoadsListItem[]>(`${environment.api}/v2/admin/driver/${userId}/completed_loads`)
      .pipe(
        map((loads) => {
          if (!loads) {
            return [];
          }
          return loads;
        }),
      );
  }

  // tslint:disable-next-line:cyclomatic-complexity
  private async loadDriverStats(driverID: string) {
    try {
      const earningsProgram = await this.authService.userInfo$
        .pipe(take(1), pluck('earningsProgramMember'))
        .toPromise();
      const superhighway = await this.featureFlagService.isFlagActive$('superhighway').pipe(take(1)).toPromise();
      const results = await this.httpClient
        .get<Stats>(`${environment.api}/${earningsProgram && superhighway ? 'v2' : 'v1'}/driver_stats/${driverID}`)
        .toPromise();
      this.driverStats$$.next(results);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadHSEScore(driverID: string) {
    try {
      const earningsProgram = await this.authService.userInfo$
        .pipe(take(1), pluck('earningsProgramMember'))
        .toPromise();
      if (!earningsProgram) {
        return;
      }
      const results = await this.httpClient
        .get<DriverHSEScore>(`${environment.api}/v2/hse/admin/${driverID}/score`)
        .toPromise();
      this.driverHSEScore$$.next(results);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadDriverCoaching() {
    try {
      const results = await this.httpClient.get<Stats>(`${environment.api}/v1/driver_stats/coaching`).toPromise();
      this.driverCoaching$$.next(results);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadDriverDetail(driverId) {
    try {
      const results = await this.httpClient
        .get<DriverDetail>(`${environment.api}/v1/user/detail/${driverId}`)
        .toPromise();
      this.driverDetails$$.next(results);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadDriverStatsRejectedLoads() {
    try {
      const results = await this.httpClient
        .get<RejectedLoads>(`${environment.api}/v1/driver_stats/rejected_loads`)
        .toPromise();
      this.driverStatsLoads$$.next(results);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadDriverStatsScheduling() {
    try {
      const results = await this.httpClient
        .get<ShiftSchedulingStats>(`${environment.api}/v1/driver_stats/shift_scheduling`)
        .toPromise();
      this.shiftStatsDrivers$$.next(results);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadAvailableDrivers() {
    const date = this.shiftFilterDate$$.value;
    const queryParams: Record<string, string> = {};
    if (date) {
      queryParams.date = date.toISOString();
    }
    try {
      const results = await this.httpClient
        .get<{ drivers: ListAvailableDriversResponseDriver[] }>(`${environment.api}/v1/users/available`, {
          params: queryParams,
        })
        .toPromise();
      this.availableDrivers$$.next(results?.drivers || []);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadAvailableDriversNow() {
    try {
      const results = await this.httpClient
        .get<{ drivers: ListAvailableDriversResponseDriver[] }>(`${environment.api}/v1/users/available`)
        .toPromise();
      this.availableDriversNow$$.next(results?.drivers || []);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadDriverCerts() {
    try {
      const results = await this.httpClient
        .get<{ certs: DriverCertification[] }>(`${environment.api}/v2/driver_certifications`)
        .toPromise();
      this.driverCerts$$.next(results?.certs || []);
    } catch (error) {
      this.logger.error(error);
    }
  }

  public async updateCerts(driverID: string, certs: number[]) {
    try {
      await this.httpClient
        .put(`${environment.api}/v2/admin/update_driver_certs/${driverID}`, {
          certs,
        })
        .toPromise();
      this.loadDriverDetail(driverID);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private async loadDriverSafetyScores() {
    try {
      const results = await this.httpClient
        .get<Record<string, HSEScore>>(`${environment.api}/v2/hse/admin/scores`)
        .toPromise();
      this.driverSafetyScores$$.next(results);
    } catch (error) {
      this.logger.error(error);
    }
  }

  private loadDriversWithRegion() {
    this.httpClient.get<DriverWithRegion[]>(`${environment.api}/v2/admin/drivers_with_region`).subscribe(
      (drivers) => {
        this.driversWithRegion$$.next(drivers || ([] as DriverWithRegion[]));
      },
      (error) => {
        this.logger.error(error);
      },
    );
  }
}

function copyDriverWithoutShiftsNotSpanningDate(date: Date): (driver: ActiveDriver) => ActiveDriver {
  return (driver: ActiveDriver): ActiveDriver => {
    return {
      ...driver,
      inProgressShift: isDateInsideShift(date, driver.inProgressShift) ? driver.inProgressShift : null,
      upcomingShift: isDateInsideShift(date, driver.upcomingShift) ? driver.upcomingShift : null,
    };
  };
}

function doDriversHaveShiftAt(date: Date): (driver: ActiveDriver) => boolean {
  return (driver: ActiveDriver): boolean => {
    return isDateInsideShift(date, driver.inProgressShift) || isDateInsideShift(date, driver.upcomingShift);
  };
}

function isDateInsideShift(date: Date, shift: LoHiShiftListItem): boolean {
  if (!date || !shift) {
    return false;
  }
  const startDate = startOfHour(new Date(shift.startTimestamp));
  const endDate = add(startOfHour(new Date(shift.endTimestamp)), { hours: 1 });
  return !isAfter(startDate, date) && isAfter(endDate, date);
}
